import { useMemo } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { applyGridPrefs } from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultTemplatesGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";
import { mergeGridPrefs } from "~/shared/utils/merge-grid-prefs.ts";

type UseTemplateGridColumnsArgs = {
	/** Base Templates columns from `useTemplateColumns`. */
	templateColumns: BoardColumn[];
	isInternal: boolean;
	/** The user's saved Templates column prefs (external users only). */
	columnPrefs?: GridPrefColumn[];
};

/**
 * Builds the Templates grid columns by merging the code-defined defaults with
 * the user's persisted column preferences (visibility, order, width).
 *
 * Mirrors `useQuoteBoardColumns`. The trailing action columns (the `isDefault`
 * toggle and the `action` buttons) are flagged `reorderable: false` by
 * `useTemplateColumns`; they are pinned to the END of the grid outside the
 * preference system, so they are excluded from the prefs pass and re-appended
 * afterward as non-reorderable, chooser-hidden columns.
 */
export const useTemplateGridColumns = ({
	templateColumns,
	isInternal,
	columnPrefs,
}: UseTemplateGridColumnsArgs): BoardColumn[] => {
	return useMemo(() => {
		const trailingColumns = templateColumns.filter(
			(col) => col.reorderable === false,
		);
		const reorderableColumns = templateColumns.filter(
			(col) => col.reorderable !== false,
		);

		const defaults =
			getDefaultTemplatesGridPrefs(isInternal)[GridIdentifiers.Templates]
				?.columns ?? [];
		const mergedPrefs = mergeGridPrefs(defaults, columnPrefs ?? []);

		const applied = applyGridPrefs(reorderableColumns, mergedPrefs, isInternal);

		const trailing = trailingColumns.map((col, index) => ({
			...col,
			show: true,
			reorderable: false,
			menu: false,
			orderIndex: applied.length + index,
		}));

		return [...applied, ...trailing];
	}, [templateColumns, isInternal, columnPrefs]);
};
